import {
  ClaimProposalDTO,
  CustomerDTO,
  OrderCustomerDTO,
  OrderManualPaymentDTO,
  OrderWithOrderCustomersDTO,
  OrderWithOrderCustomersDTOStatusIdEnum,
  PaymentPlanDTO,
} from '@reposit/api-client';
import { get } from 'lodash';
import { ClaimProposalEntity } from '../entities/entities.types';
import { AppState } from '../root.reducer';
import { getFullClaimById } from '../selectors/claim.selectors';
import { getFirstAgentProposal, getFirstTenantProposal, getSecondAgentProposal } from '../selectors/mediation.selectors';

const getPendingOrderFromProposal = (
  proposal?: ClaimProposalDTO | ClaimProposalEntity,
): OrderWithOrderCustomersDTO | undefined => {
  const order = get(proposal, 'order') as OrderWithOrderCustomersDTO | undefined;
  return order && order.statusId === OrderWithOrderCustomersDTOStatusIdEnum.PENDING ? order : undefined;
};

const getLatestPendingProposalOrder = (state: AppState, claimId: string): OrderWithOrderCustomersDTO | undefined => {
  const pendingOrders = [
    getPendingOrderFromProposal(getSecondAgentProposal(state, claimId)),
    getPendingOrderFromProposal(getFirstTenantProposal(state, claimId)),
    getPendingOrderFromProposal(getFirstAgentProposal(state, claimId)),
  ];

  return pendingOrders.find((order) => !!order);
};

const getOrderCustomerWithPaymentPlanFromProposal = (
  proposal?: ClaimProposalDTO | ClaimProposalEntity,
): OrderCustomerDTO | undefined => {
  const orderCustomers: OrderCustomerDTO[] = get(proposal, 'order.orderCustomers', []);
  return orderCustomers.find((oc) => oc.paymentPlan);
};

const getManualPaymentsFromProposal = (
  proposal?: ClaimProposalDTO | ClaimProposalEntity,
): OrderManualPaymentDTO[] | undefined => {
  return get(proposal, 'order.manualPayments');
};

export const getPendingPaymentPlanOrder =
  (claimId: string) =>
  (state: AppState): OrderWithOrderCustomersDTO | undefined => {
    const claim = getFullClaimById(state, claimId);
    if (!claim) {
      return undefined;
    }
    const arbitrationRepaymentOrder = claim.arbitration ? claim.arbitration.repaymentOrder : undefined;
    if (arbitrationRepaymentOrder) {
      // Once arbitration has a repayment order, proposal orders are historical and cannot be the active order.
      return arbitrationRepaymentOrder.statusId === OrderWithOrderCustomersDTOStatusIdEnum.PENDING
        ? arbitrationRepaymentOrder
        : undefined;
    }

    return getLatestPendingProposalOrder(state, claimId);
  };

const getOrderCustomerWithPaymentPlanByClaimId =
  (claimId: string) =>
  (state: AppState): OrderCustomerDTO | undefined => {
    const claim = getFullClaimById(state, claimId);
    if (!claim) {
      return undefined;
    }
    const arbOrderCustomers: OrderCustomerDTO[] = get(claim, 'arbitration.repaymentOrder.orderCustomers', []);
    if (arbOrderCustomers.length) {
      const ocWithPaymentPlan = arbOrderCustomers.find((oc) => oc.paymentPlan);
      return ocWithPaymentPlan;
    } else {
      // latest proposal
      const firstAgentProposal = claim.proposals.find((p) => p.round === 1);
      const firstTenantProposal = claim.proposals.find((p) => p.round === 2);
      const secondAgentProposal = claim.proposals.find((p) => p.round === 3);

      return (
        getOrderCustomerWithPaymentPlanFromProposal(secondAgentProposal) ||
        getOrderCustomerWithPaymentPlanFromProposal(firstTenantProposal) ||
        getOrderCustomerWithPaymentPlanFromProposal(firstAgentProposal)
      );
    }
  };

export const getPaymentPlanByClaimId =
  (claimId: string) =>
  (state: AppState): PaymentPlanDTO | undefined => {
    const orderCustomerWithPaymentPlan = getOrderCustomerWithPaymentPlanByClaimId(claimId)(state);
    if (!orderCustomerWithPaymentPlan) return undefined;
    return get(orderCustomerWithPaymentPlan, 'paymentPlan');
  };

export const getPaymentPlanCustomerByClaimId =
  (claimId: string) =>
  (state: AppState): CustomerDTO | undefined => {
    const orderCustomerWithPaymentPlan = getOrderCustomerWithPaymentPlanByClaimId(claimId)(state);
    if (!orderCustomerWithPaymentPlan) return undefined;
    return get(orderCustomerWithPaymentPlan, 'customer');
  };

export const getManualPaymentsByClaimId =
  (claimId: string) =>
  (state: AppState): OrderManualPaymentDTO[] => {
    const claim = getFullClaimById(state, claimId);

    if (get(claim, 'arbitration.repaymentOrder')) {
      return get(claim, 'arbitration.repaymentOrder.manualPayments', []);
    }

    const firstAgentProposal = getFirstAgentProposal(state, claimId);
    const firstTenantProposal = getFirstTenantProposal(state, claimId);
    const secondAgentProposal = getSecondAgentProposal(state, claimId);

    return (
      getManualPaymentsFromProposal(secondAgentProposal) ||
      getManualPaymentsFromProposal(firstTenantProposal) ||
      getManualPaymentsFromProposal(firstAgentProposal) ||
      []
    );
  };
